import * as React from 'react';
import { Money } from '../../../components/ui/Money.js';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale.js';
import type { PrizePayoutDetail, PayoutStatus } from '../network/payouts.api.js';
import { useApprovePayout } from '../network/payouts.api.js';
import { DividendCreditsTable } from './DividendCreditsTable.js';
import { Button } from '../../../components/ui/Button.js';
import { StatusBadge } from '../../../components/ui/StatusBadge.js';
import { useToast } from '../../../components/ui/Toast.js';
import { getSession } from '../../../lib/session.js';

type ChainStep = {
  key: string;
  label: string;
  done: boolean;
  blocking: boolean;
  blocker?: string;
};

function buildChain(status: PayoutStatus, suretyClearedAt: string | null): ChainStep[] {
  const surety = suretyClearedAt !== null;
  return [
    {
      key: 'auction',
      label: 'Auction confirmed',
      done: true,
      blocking: false,
    },
    {
      key: 'surety',
      label: 'Surety cleared',
      done: surety,
      blocking: !surety && status === 'PENDING_SURETY',
      blocker: 'Waiting on surety nominees — check Surety Requests',
    },
    {
      key: 'approval',
      label: 'Authorized / Approved',
      done: ['APPROVED', 'INSTRUCTION_CREATED', 'PAID'].includes(status),
      blocking: status === 'PENDING_APPROVAL',
      blocker: 'Pending verification / approval',
    },
    {
      key: 'instruction',
      label: 'Money instruction created',
      done: ['INSTRUCTION_CREATED', 'PAID'].includes(status),
      blocking: false,
    },
    {
      key: 'paid',
      label: 'Paid',
      done: status === 'PAID',
      blocking: false,
    },
  ];
}

interface PayoutDetailContentProps {
  payout: PrizePayoutDetail | undefined;
}

export function PayoutDetailContent({ payout }: PayoutDetailContentProps) {
  const [showDividends, setShowDividends] = React.useState(false);
  const session = getSession();
  const canReadDividend = session?.permissions?.includes('chitfunds.dividend.read') ?? false;
  const canApprovePayout = session?.permissions?.includes('chitfunds.payout.approve') ?? false;
  const approvePayout = useApprovePayout();
  const { toast } = useToast();

  if (!payout) return null;

  // Blocker 2 (V1 gap plan): PENDING_APPROVAL -> APPROVED was unreachable from any client.
  // This action closes that gap. The surety gate stays visible: approval is disabled while
  // surety has not cleared, mirroring the API's status machine (PENDING_SURETY must clear
  // before PENDING_APPROVAL is ever reached).
  const showApproveAction =
    canApprovePayout && (payout.status as PayoutStatus) === 'PENDING_APPROVAL';
  const suretyCleared = payout.suretyClearedAt !== null;

  const handleApprove = () => {
    approvePayout.mutate(payout.id, {
      onSuccess: () => {
        toast({
          type: 'success',
          title: 'Payout Approved',
          description: 'Money instruction created for the winner’s net prize.',
        });
      },
      onError: (err: Error) => {
        toast({
          type: 'error',
          title: 'Approval Failed',
          description: err?.message || 'Could not approve this prize payout.',
        });
      },
    });
  };

  const chain = buildChain(payout.status as PayoutStatus, payout.suretyClearedAt);
  const blockingStep = chain.find((s) => s.blocking);

  return (
    <>
      {blockingStep && (
        <div className="flex items-start gap-3 p-4 bg-warning-bg border border-warning-text/20 rounded-xl">
          <svg className="w-5 h-5 text-warning-text flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <div>
            <p className="text-sm font-bold text-warning-text">Blocked</p>
            <p className="text-xs text-warning-text mt-0.5">{blockingStep.blocker}</p>
            {blockingStep.key === 'approval' && (
              <a
                href="/approvals?type=PAYOUT_REVERSED"
                className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-accent-500 hover:text-accent-600 underline underline-offset-2"
              >
                Go to Approvals Queue →
              </a>
            )}
          </div>
        </div>
      )}

      {showApproveAction && (
        <div className="p-4 bg-surface-2 border border-border rounded-xl">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-text-primary">Checker Approval</p>
              <p className="text-xs text-text-secondary mt-0.5">
                {suretyCleared
                  ? 'Surety cleared — approving creates the money instruction.'
                  : 'Surety not yet cleared — approval is blocked until all sureties are approved.'}
              </p>
            </div>
            <StatusBadge status={suretyCleared ? 'success' : 'warning'} label={suretyCleared ? 'Surety Cleared' : 'Surety Pending'} dot={false} />
          </div>
          <div className="mt-3">
            <Button
              variant="primary"
              size="sm"
              onClick={handleApprove}
              loading={approvePayout.isPending}
              disabled={!suretyCleared}
            >
              Approve Payout
            </Button>
          </div>
        </div>
      )}

      <div className="bg-surface-2 rounded-xl border border-border divide-y divide-border">
        <div className="px-4 py-3 flex justify-between items-center text-sm">
          <span className="text-text-secondary font-medium">Gross Prize</span>
          <span className="font-bold text-text-primary">
            <Money paise={payout.grossPrize} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
          </span>
        </div>
        <div className="px-4 py-3 flex justify-between items-center text-sm">
          <span className="text-text-secondary font-medium">TDS</span>
          <span className="font-semibold text-danger-text">
            −<Money paise={payout.tdsOnPrize} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
          </span>
        </div>
        <div className="px-4 py-3 flex justify-between items-center text-sm">
          <span className="text-text-secondary font-medium">Net Payout</span>
          <span className="font-bold text-text-primary text-base">
            <Money paise={payout.netPayout} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
          </span>
        </div>
      </div>

      <div>
        <h3 className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-3">
          Payout Chain
        </h3>
        <ol className="relative border-l border-border space-y-4 pl-6">
          {chain.map((step) => (
            <li key={step.key} className="relative">
              <span
                className={[
                  'absolute -left-[25px] flex items-center justify-center w-4 h-4 rounded-full border-2 mt-0.5',
                  step.done
                    ? 'bg-success-500 border-success-500'
                    : step.blocking
                    ? 'bg-warning-bg border-warning-text'
                    : 'bg-surface-1 border-border',
                ].join(' ')}
                aria-hidden="true"
              >
                {step.done && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                  </svg>
                )}
              </span>
              <p className={[
                'text-sm font-medium',
                step.done ? 'text-text-primary' : step.blocking ? 'text-warning-text' : 'text-text-muted',
              ].join(' ')}>
                {step.label}
              </p>
              {step.blocking && step.key === 'approval' && (
                <a
                  href="/approvals?type=PAYOUT_REVERSED"
                  className="mt-0.5 text-xs font-semibold text-accent-500 hover:text-accent-600"
                >
                  View in Approvals Queue →
                </a>
              )}
            </li>
          ))}
        </ol>
      </div>

      <div className="bg-surface-2 rounded-xl border border-border divide-y divide-border">
        <div className="px-4 py-2.5 flex justify-between items-center text-xs">
          <span className="text-text-secondary font-medium">Payout ID</span>
          <span className="font-mono text-text-primary">{payout.id.slice(0, 8)}…</span>
        </div>
        <div className="px-4 py-2.5 flex justify-between items-center text-xs">
          <span className="text-text-secondary font-medium">Auction ID</span>
          <span className="font-mono text-text-primary">{payout.auctionId.slice(0, 8)}…</span>
        </div>
        {payout.instructionId && (
          <div className="px-4 py-2.5 flex justify-between items-center text-xs">
            <span className="text-text-secondary font-medium">Money Instruction</span>
            <span className="font-mono text-text-primary">{payout.instructionId.slice(0, 8)}…</span>
          </div>
        )}
        <div className="px-4 py-2.5 flex justify-between items-center text-xs">
          <span className="text-text-secondary font-medium">Created</span>
          <span className="text-text-secondary">{new Date(payout.createdAt).toLocaleString()}</span>
        </div>
      </div>

      {canReadDividend && (
        <div>
          <Button
            variant="ghost"
            onClick={() => setShowDividends((v) => !v)}
            className="flex items-center gap-2 text-sm font-bold text-text-primary hover:text-accent-500 transition-colors bg-transparent hover:bg-transparent p-0"
          >
            <span>Dividend Credits (this subscriber)</span>
            <svg
              className={['w-4 h-4 transition-transform', showDividends ? 'rotate-180' : ''].join(' ')}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </Button>
          {showDividends && (
            <div className="mt-3">
              <DividendCreditsTable subscriberId={payout.winnerSubscriptionId} />
            </div>
          )}
        </div>
      )}
    </>
  );
}